import { useState, useEffect } from 'react';
import { Edit, Trash2, Eye, X, ArrowLeft } from 'lucide-react';
import '../css/merchent.css';
import axiosInstance from '../../../axiosConfig';

export default function MerchantInformation({ isEditMode, entityId, entityUuid, entityName, entityType, entityCode, initialBranches = [], setActiveTab }) { // Accept entity data props
  const [showBranchListView, setShowBranchListView] = useState(false);
  const [branchForms, setBranchForms] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isSubmittingAll, setIsSubmittingAll] = useState(false); // Add loading state for submit all
  const [editingBranch, setEditingBranch] = useState(null); // Track which branch is being edited

  // Use props for merchant info, with fallbacks for compatibility
  const merchantInfo = {
    name: entityName || 'Merchant Name',
    registration: entityCode || 'N/A',
    type: entityType || 'N/A',
    uuid: entityUuid || '',
    id: entityId || '',
  };

  // Fetch branches from API
  const filterBranchesForEntity = (list) => {
    if (!Array.isArray(list)) return [];
    const hasEntityId = entityId != null && entityId !== '';
    const hasEntityUuid = !!entityUuid;
    if (!hasEntityId && !hasEntityUuid) return list;
    return list.filter((b) => {
      if (hasEntityId && String(b.entityId) === String(entityId)) return true;
      if (hasEntityUuid && String(b.entityUuid) === String(entityUuid)) return true;
      return false;
    });
  };

  const fetchBranches = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axiosInstance.get('/internal-api/v1/client-web/entity-panel/entity-branch/config/list');
      if (response.data && response.data.data) {
        const listBranches = filterBranchesForEntity(response.data.data);
        if (Array.isArray(initialBranches) && initialBranches.length > 0) {
          const filteredInitial = filterBranchesForEntity(initialBranches);
          const byId = new Map(filteredInitial.map((b) => [b.id, b]));
          const merged = listBranches.map((b) => byId.get(b.id) || b);
          const extra = filteredInitial.filter((b) => !merged.find((m) => m.id === b.id));
          setBranches([...merged, ...extra]);
        } else {
          setBranches(listBranches);
        }
      }
    } catch (error) {
      console.error('Error fetching branches:', error);
      setError('Failed to load branches');
    } finally {
      setLoading(false);
    }
  };

  // Fetch branches when branch list view opens
  useEffect(() => {
    if (showBranchListView) {
      fetchBranches();
    }
  }, [showBranchListView]);

  // Fetch branches for current entity when component mounts
  useEffect(() => {
    if (entityId) {
      fetchBranches();
    }
  }, [entityId]);

  useEffect(() => {
    if (Array.isArray(initialBranches) && initialBranches.length > 0) {
      setBranches(filterBranchesForEntity(initialBranches));
    }
  }, [initialBranches, entityId, entityUuid]);

  const addNewBranchForm = () => {
    const newFormId = Date.now();
    setBranchForms([...branchForms, newFormId]);
    setShowBranchListView(false);
    setEditingBranch(null); // Clear editing state for new branch
  };

  const editBranch = (branch) => {
    const detailedBranch =
      (Array.isArray(initialBranches) &&
        (initialBranches.find((b) => b.id === branch.id) ||
          initialBranches.find((b) => b.uuid && b.uuid === branch.uuid))) ||
      branch;
    setEditingBranch(detailedBranch);
    const newFormId = Date.now();
    setBranchForms([...branchForms, newFormId]);
    setShowBranchListView(false);
  };

  const removeBranchForm = (formId) => {
    setBranchForms(branchForms.filter((id) => id !== formId));
  };

  // Delete branch (call state-update API with mode REMOVE)
  const handleDeleteBranch = async (branch) => {
    if (!branch || !branch.id) return alert('Branch id is missing');
    const ok = window.confirm(`Delete branch "${branch.name || branch.id}"? This cannot be undone.`);
    if (!ok) return;

    try {
      setLoading(true);
      const payload = { id: branch.id, state: false, mode: 'REMOVE' };
      const res = await axiosInstance.post('/internal-api/v1/client-web/entity-panel/entity-branch/config/state-update', payload);
      if (res.data && (res.data.status || res.data.result)) {
        alert('Branch deleted successfully');
        fetchBranches();
      } else {
        console.error('Delete failed', res);
        alert('Failed to delete branch.');
      }
    } catch (e) {
      console.error('Delete error', e);
      alert('Error deleting branch.');
    } finally {
      setLoading(false);
    }
  };

  // Updated handleSubmitAll function to navigate to next tab
  const handleSubmitAll = async () => {
    setIsSubmittingAll(true);
    
    try {
      console.log('Submitting all branches and proceeding to next step...');
      
      // If there are no branch forms to submit, just navigate to next tab
      if (branchForms.length === 0) {
        console.log('No branch forms to submit, proceeding to next tab');
        if (setActiveTab) {
          setActiveTab("images"); // Navigate to Document tab
        }
        return;
      }

      // Here you could add logic to submit all pending branch forms if needed
      // For now, we'll just simulate success and navigate
      
      // Simulate API call delay
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      console.log('All branches submitted successfully');
      
      // Navigate to next tab (Document tab)
      if (setActiveTab) {
        setActiveTab("images");
      }
      
    } catch (error) {
      console.error('Error submitting all branches:', error);
      // You could show an error message here
    } finally {
      setIsSubmittingAll(false);
    }
  };

  const handleShowBranchList = () => {
    setShowBranchListView(true);
    setBranchForms([]);
  };

  const handleCloseBranchList = () => {
    setShowBranchListView(false);
  };

  const BranchForm = ({ formId, onRemove }) => {
    const isEditMode = editingBranch !== null; // Check if we're in edit mode
    
    const [formData, setFormData] = useState({
      entityId: entityId, 
      name: '',
      branchCode: '',
      geoLocation: {
        name: '',
        latitude: '',
        longitude: '',
        altitude: '',
        address: '',
      },
      fullAddress: '',
      website: '',
      contactNos: [''],
      emails: [''],
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [formError, setFormError] = useState(null);

    // Load branch data if editing
    useEffect(() => {
      if (isEditMode && editingBranch) {
        const decodedWebsite = editingBranch.website ? decodeURIComponent(editingBranch.website) : '';
        setFormData({
          id: editingBranch.id, // Include id for update
          entityId: editingBranch.entityId || entityId,
          name: editingBranch.name || '',
          branchCode: editingBranch.branchCode || '',
          geoLocation: editingBranch.geoLocation || {
            name: '',
            latitude: '',
            longitude: '',
            altitude: '',
            address: '',
          },
          fullAddress: editingBranch.fullAddress || '',
          website: decodedWebsite,
          contactNos: editingBranch.contactNos && editingBranch.contactNos.length > 0 ? editingBranch.contactNos : [''],
          emails: editingBranch.emails && editingBranch.emails.length > 0 ? editingBranch.emails : [''],
        });
      }
    }, [isEditMode, editingBranch]);

    // Update entityId in formData when entityId prop changes
    useEffect(() => {
      if (entityId && !isEditMode) {
        setFormData(prevData => ({
          ...prevData,
          entityId: entityId
        }));
      }
    }, [entityId, isEditMode]);

    const handleChange = (e) => {
      const { name, value } = e.target;

      if (name.startsWith('geoLocation.')) {
        const geoField = name.split('.')[1];
        setFormData((prevData) => ({
          ...prevData,
          geoLocation: {
            ...prevData.geoLocation,
            [geoField]: value,
          },
        }));
      } else {
        setFormData((prevData) => ({
          ...prevData,
          [name]: value,
        }));
      }
    };

    const handleContactNumberChange = (index, value) => {
      const updatedContacts = [...formData.contactNos];
      updatedContacts[index] = value;
      setFormData(prevData => ({
        ...prevData,
        contactNos: updatedContacts
      }));
    };

    const handleAddContactNumber = () => {
      setFormData(prevData => ({
        ...prevData,
        contactNos: [...prevData.contactNos, '']
      }));
    };

    const handleRemoveContactNumber = (index) => {
      const updatedContacts = formData.contactNos.filter((_, i) => i !== index);
      setFormData(prevData => ({
        ...prevData,
        contactNos: updatedContacts
      }));
    };

    const handleEmailChange = (index, value) => {
      const updatedEmails = [...formData.emails];
      updatedEmails[index] = value;
      setFormData(prevData => ({
        ...prevData,
        emails: updatedEmails
      }));
    };

    const handleAddEmail = () => {
      setFormData(prevData => ({
        ...prevData,
        emails: [...prevData.emails, '']
      }));
    };

    const handleRemoveEmail = (index) => {
      const updatedEmails = formData.emails.filter((_, i) => i !== index);
      setFormData(prevData => ({
        ...prevData,
        emails: updatedEmails
      }));
    };

    const handleSubmit = async (e) => {
      e.preventDefault();
      setIsSubmitting(true);
      setFormError(null);

      try {
        let response;
        
        if (isEditMode) {
          // UPDATE MODE - use PUT endpoint
          const updateData = {
            id: formData.id,
            name: formData.name,
            branchCode: formData.branchCode || undefined,
            geoLocation: {
              name: formData.geoLocation.name || undefined,
              latitude: formData.geoLocation.latitude,
              longitude: formData.geoLocation.longitude,
              altitude: formData.geoLocation.altitude || undefined,
              address: formData.geoLocation.address || undefined,
            },
            fullAddress: formData.fullAddress || undefined,
            website: formData.website || undefined,
            contactNos: formData.contactNos.filter(contact => contact.trim() !== ''),
            emails: formData.emails.filter(email => email.trim() !== ''),
          };

          response = await axiosInstance.put(
            '/internal-api/v1/client-web/entity-panel/registration/update-entity-branch',
            updateData
          );
          console.log('Branch updated:', response.data);
        } else {
          // CREATE MODE - use POST endpoint
          response = await axiosInstance.post(
            '/internal-api/v1/client-web/entity-panel/registration/add-entity-branch',
            formData
          );
          console.log('Branch created:', response.data);
        }
        
        // On successful submission, close the form and refresh branch list
        onRemove(formId); // Close the form
        setEditingBranch(null); // Clear editing state
        fetchBranches(); // Always refresh branches after saving
        
        alert(isEditMode ? 'Branch updated successfully!' : 'Branch created successfully!');
      } catch (error) {
        console.error('Error submitting form:', error);
        setFormError(error.message || 'Failed to submit branch');
      } finally {
        setIsSubmitting(false);
      }
    };


    return (
      <div className="branch-form-container">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base font-semibold text-gray-800">
            {isEditMode ? 'Edit Branch' : 'Add New Branch'}
          </h3>
          <button className="modal-close-button" onClick={() => onRemove(formId)}>
            <X size={18} />
          </button>
        </div>

        <div className="info-box">
          {formError && <div className="mb-4 text-red-600">{formError}</div>}
          {/* Display current entityId for debugging */}
          {entityId && (
            <div className="mb-4 text-sm text-gray-600">
              Entity ID: {entityId}
            </div>
          )}
          <div className="form-grid">
            <div>
              <label className="form-label">
                Branch Name <span className="required-field">*</span>
              </label>
              <input
                name="name"
                value={formData.name}
                onChange={handleChange}
                type="text"
                placeholder="Enter branch name"
                className="form-input"
                required
              />
            </div>
            <div>
              <label className="form-label">Branch Code</label>
              <input
                name="branchCode"
                value={formData.branchCode}
                onChange={handleChange}
                type="text"
                placeholder="Enter branch code"
                className="form-input"
              />
            </div>
            <div>
              <label className="form-label">Location Name</label>
              <input
                name="geoLocation.name"
                value={formData.geoLocation.name}
                onChange={handleChange}
                type="text"
                placeholder="Enter location name"
                className="form-input"
              />
            </div>
            <div>
              <label className="form-label">Latitude</label>
              <input
                name="geoLocation.latitude"
                value={formData.geoLocation.latitude}
                onChange={handleChange}
                type="text"
                placeholder="Enter latitude"
                className="form-input"
              />
            </div>
            <div>
              <label className="form-label">Longitude</label>
              <input
                name="geoLocation.longitude"
                value={formData.geoLocation.longitude}
                onChange={handleChange}
                type="text"
                placeholder="Enter longitude"
                className="form-input"
              />
            </div>
            <div>
              <label className="form-label">Altitude</label>
              <input
                name="geoLocation.altitude"
                value={formData.geoLocation.altitude}
                onChange={handleChange}
                type="text"
                placeholder="Enter altitude"
                className="form-input"
              />
            </div>
            <div>
              <label className="form-label">Full Address</label>
              <input
                name="fullAddress"
                value={formData.fullAddress}
                onChange={handleChange}
                type="text"
                placeholder="Enter full address"
                className="form-input"
              />
            </div>
            <div>
              <label className="form-label">Website</label>
              <input
                name="website"
                value={formData.website}
                onChange={handleChange}
                type="text"
                placeholder="Enter website (optional)"
                className="form-input"
              />
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="form-label">Email Addresses</label>
                <button
                  type="button"
                  onClick={handleAddEmail}
                  className="flex items-center px-2 py-1 text-xs text-blue-600 border border-blue-600 rounded hover:bg-blue-50"
                >
                  <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4"></path>
                  </svg>
                  Add
                </button>
              </div>
              <div className="space-y-2">
                {formData.emails.map((email, index) => (
                  <div key={index} className="flex gap-2">
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => handleEmailChange(index, e.target.value)}
                      placeholder="Enter branch email"
                      className="flex-1 form-input"
                    />
                    {formData.emails.length > 1 && (
                      <button
                        type="button"
                        onClick={() => handleRemoveEmail(index)}
                        className="px-2 py-1 text-xs text-red-600 border border-red-600 rounded hover:bg-red-50"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                        </svg>
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="form-label">Phone Numbers</label>
                <button
                  type="button"
                  onClick={handleAddContactNumber}
                  className="flex items-center px-2 py-1 text-xs text-blue-600 border border-blue-600 rounded hover:bg-blue-50"
                >
                  <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4"></path>
                  </svg>
                  Add
                </button>
              </div>
              <div className="space-y-2">
                {formData.contactNos.map((contact, index) => (
                  <div key={index} className="flex gap-2">
                    <input
                      type="tel"
                      value={contact}
                      onChange={(e) => handleContactNumberChange(index, e.target.value)}
                      placeholder="Enter phone number"
                      className="flex-1 form-input"
                    />
                    {formData.contactNos.length > 1 && (
                      <button
                        type="button"
                        onClick={() => handleRemoveContactNumber(index)}
                        className="px-2 py-1 text-xs text-red-600 border border-red-600 rounded hover:bg-red-50"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                        </svg>
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
            
          </div>

          <div className="action-buttons">
            <button className="button button-secondary" onClick={() => onRemove(formId)}>
              Close
            </button>
            <button
              className={`button button-primary ${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''}`}
              onClick={handleSubmit}
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Saving...' : (isEditMode ? 'Update Branch' : 'Save This Branch')}
            </button>
          </div>
        </div>
      </div>
    );
  };

  const BranchListView = () => {
    return (
      <div className="branch-list-container">
        <div className="flex items-center justify-between mb-4">
          {/* <div className="flex items-center">
            <button
              className="flex items-center mr-4 text-gray-600 hover:text-gray-800"
              onClick={handleCloseBranchList}
            >
              <ArrowLeft size={18} className="mr-1" />
              Back
            </button>
            <h3 className="text-base font-semibold text-gray-800">Branch List</h3>
          </div> */}
          <button className="button button-primary" onClick={addNewBranchForm}>
            <span className="mr-1">+</span> Add New Branch
          </button>
        </div>

        <div className="info-box">
          {loading && (
            <div className="py-8 text-center">
              <div className="text-gray-600">Loading branches...</div>
            </div>
          )}

          {error && (
            <div className="py-8 text-center">
              <div className="mb-4 text-red-600">{error}</div>
              <button onClick={fetchBranches} className="button button-primary">
                Retry
              </button>
            </div>
          )}

          {!loading && !error && (
            <>
              <div className="table-container">
                <table className="w-full data-table">
                  <thead>
                    <tr className="table-header">
                      <th className="table-header-cell">Branch Name</th>
                      <th className="table-header-cell">Branch ID</th>
                      <th className="table-header-cell">UUID</th>
                      <th className="table-header-cell">Entity ID</th>
                      <th className="table-header-cell">Created At</th>
                      <th className="table-header-cell">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {branches.length > 0 ? (
                      branches.map((branch) => (
                        <tr key={branch.id} className="table-row">
                          <td className="table-cell">
                            <div className="font-medium">{branch.name}</div>
                          </td>
                          <td className="table-cell">{branch.id}</td>
                          <td className="table-cell">
                            <div className="font-mono text-xs text-gray-500">{branch.uuid}</div>
                          </td>
                          <td className="table-cell">{branch.entityId}</td>
                          <td className="table-cell">
                            <div className="text-xs">{new Date(branch.createdAt).toLocaleDateString()}</div>
                            <div className="text-xs text-gray-500">{new Date(branch.createdAt).toLocaleTimeString()}</div>
                          </td>
                          <td className="table-cell">
                            <div className="flex space-x-2">
                              <button 
                                className="text-gray-500 hover:text-gray-700"
                                onClick={() => alert('View functionality coming soon')}
                                title="View"
                              >
                                <Eye size={16} />
                              </button>
                              <button 
                                className="text-blue-500 hover:text-blue-700"
                                onClick={() => editBranch(branch)}
                                title="Edit"
                              >
                                <Edit size={16} />
                              </button>
                              <button 
                                className="text-red-500 hover:text-red-700"
                                onClick={() => alert('Delete functionality will be enabled in a future update')}
                                title="Delete (Future)"
                              >
                                <Trash2 size={16} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="6" className="table-cell py-8 text-center text-gray-500">
                          No branches found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              <div className="mt-4 pagination">
                <div className="text-sm text-gray-600">
                  Showing 1-{branches.length} of {branches.length} branches
                </div>
                <div className="pagination-buttons">
                  <button className="pagination-button">&lt;</button>
                  <button className="pagination-button pagination-button-active">1</button>
                  <button className="pagination-button">&gt;</button>
                </div>
              </div>
            </>
          )}

          <div className="mt-4 action-buttons">
            <button className="button button-secondary" onClick={handleCloseBranchList}>
              Close
            </button>
            <button className="button button-primary" onClick={fetchBranches}>
              Refresh
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="merchant-container">
      {/* Show info message if no entity data is available */}
      {!entityId && !entityUuid ? (
        <div className="p-6">
          <div className="p-6 text-center rounded bg-blue-50">
            <h3 className="mb-3 text-base font-medium text-gray-700">Branches Management</h3>
            <p className="mb-2 text-sm text-gray-600">Please complete the Basic Info step first to enable branch management.</p>
            <p className="mt-3 text-xs text-gray-600">After saving the merchant, you will be able to add branch locations with:</p>
            <p className="mt-2 text-xs text-gray-600">
              • Branch address/location details • Contact information • Branch-specific settings
            </p>
          </div>
        </div>
      ) : (
        <>
          <div className="merchant-header">
            <h3 className="merchant-title">{merchantInfo.name}</h3>
            <div className="merchant-subtitle">
              Registration: {merchantInfo.registration}
            </div>
            <div className="merchant-subtitle">
              Type: {merchantInfo.type}
            </div>
            {/* {merchantInfo.uuid && (
              <div className="text-xs text-gray-500 merchant-subtitle">
                UUID: {merchantInfo.uuid} | Entity ID: {merchantInfo.id}
              </div>
            )} */}
          </div>

          {!showBranchListView && (
            <div className="tab-navigation">
              <div className="flex space-x-2">
                {/* <button className="tab-button" onClick={handleShowBranchList}>
                  <span className="">📋 Branch List</span>
                </button> */}
                <button className="tab-button tab-button-primary" onClick={addNewBranchForm}>
                  <span className="mr-1">+</span> Add New Branch
                </button>
              </div>
            </div>
          )}

          <div className="p-4">
            {showBranchListView && <BranchListView />}
            {!showBranchListView &&
              branchForms.map((formId) => (
                <BranchForm key={formId} formId={formId} onRemove={removeBranchForm} />
              ))}
          </div>

          {/* Branches Section - Show current entity branches */}
          {!showBranchListView && branchForms.length === 0 && (
            <div className="px-4 pb-4">
              <div className="info-box">
                <h4 className="mb-4 text-base font-semibold text-gray-800">Branches</h4>
                
                {loading && (
                  <div className="py-8 text-center">
                    <div className="text-gray-600">Loading branches...</div>
                  </div>
                )}

                {error && (
                  <div className="py-8 text-center">
                    <div className="mb-4 text-red-600">{error}</div>
                    <button onClick={fetchBranches} className="button button-primary">
                      Retry
                    </button>
                  </div>
                )}

                {!loading && !error && (
                  <>
                    {branches.filter(branch => branch.entityId === entityId).length > 0 ? (
                      <div className="table-container">
                        <table className="w-full data-table">
                          <thead>
                            <tr className="table-header">
                              <th className="table-header-cell">Branch Name</th>
                              <th className="table-header-cell">Branch ID</th>
                              <th className="table-header-cell">UUID</th>
                              <th className="table-header-cell">Entity ID</th>
                              {isEditMode && branchForms.length === 0 && <th className="table-header-cell">Actions</th>}
                            </tr>
                          </thead>
                          <tbody>
                            {branches
                              .filter(branch => branch.entityId === entityId)
                              .map((branch) => (
                                <tr key={branch.id} className="table-row">
                                  <td className="table-cell">
                                    <div className="font-medium">{branch.name}</div>
                                  </td>
                                  <td className="table-cell">{branch.id}</td>
                                  <td className="table-cell">
                                    <div className="font-mono text-xs text-gray-500">{branch.uuid}</div>
                                  </td>
                                  <td className="table-cell">{branch.entityId}</td>
                                  {isEditMode && branchForms.length === 0 && (
                                    <td className="table-cell">
                                      <div className="flex space-x-2">
                                        {/* <button 
                                          className="text-gray-500 hover:text-gray-700"
                                          onClick={() => alert('View functionality coming soon')}
                                          title="View"
                                        >
                                          <Eye size={16} />
                                        </button> */}
                                        <button 
                                          className="text-blue-500 hover:text-blue-700"
                                          onClick={() => editBranch(branch)}
                                          title="Edit"
                                        >
                                          <Edit size={16} />
                                        </button>
                                        <button 
                                          className="text-red-500 hover:text-red-700"
                                          onClick={() => handleDeleteBranch(branch)}
                                          title="Delete"
                                        >
                                          <Trash2 size={16} />
                                        </button>
                                      </div>
                                    </td>
                                  )}
                                </tr>
                              ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div className="py-8 text-sm text-center text-gray-500">
                        No branches found for this entity. Click "Add New Branch" to create one.
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          )}

          {!showBranchListView && branchForms.length > 0 && (
            <div className="px-4 pb-4">
              <div className="info-box">
                <h4 className="info-title">Branch Management Information</h4>
                <ul className="info-list">
                  <li>Branches can have different operating hours and contact details than the main office</li>
                  <li>Each branch can be assigned specific permissions and access controls in the system</li>
                </ul>
              </div>
            </div>
          )}

          {/* Updated action buttons section with navigation functionality */}
          {!showBranchListView && (
            <div className="action-buttons">
              <button className="button button-secondary">
                Cancel
              </button>
              <button 
                onClick={handleSubmitAll} 
                className={`button button-primary ${isSubmittingAll ? 'opacity-50 cursor-not-allowed' : ''}`}
                disabled={isSubmittingAll}
              >
                {isSubmittingAll ? 'Submitting...' : 'Next: Document'}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
